import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useRoleContext } from "hooks";
import { useWriterComponentState } from "hooks/useWriterComponentState";
import { validateEmail } from "utils";

import { ClientAppEvent } from "../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
}

export function AddCompanyMemberModal(props: Props) {
  const { open, onClose, onSuccess = () => {} } = props;
  const { getAdminRole } = useRoleContext();
  const posthog = usePostHog();

  const [formData, setFormData] = useState<FormData>({
    email: "",
    firstName: "",
    lastName: "",
  });

  const [validationErrors, setValidationErrors] = useState<Partial<FormData>>(
    {},
  );

  const adminRole = getAdminRole();
  const companyId = adminRole?.company?.id;

  const { loading, error, write, resetState } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: `/companies/${companyId}/members/create`,
      body: {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
      },
    },
    errorMessage: "Failed to add team member",
  });

  const validateForm = (): Partial<FormData> => {
    const errors: Partial<FormData> = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        errors.email = emailError;
      }
    }

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      write(() => {
        posthog?.capture(ClientAppEvent.TEAM_MEMBER_ADDED);
        onSuccess();
      });
    }
  };

  const handleClose = () => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
    });
    setValidationErrors({});
    resetState();
    onClose();
  };

  const handleMemberFormChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const isFormValid = Object.keys(validateForm()).length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h5">Add Team Member</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Invite your teammates to join you on Evotrux. Each member will
            receive an email invitation.
          </Typography>

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleMemberFormChange("email", e.target.value)}
            required
            fullWidth
            placeholder="teammate@company.com"
            error={!!validationErrors.email}
            helperText={validationErrors.email}
          />

          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e) =>
              handleMemberFormChange("firstName", e.target.value)
            }
            required
            fullWidth
            placeholder="John"
            error={!!validationErrors.firstName}
            helperText={validationErrors.firstName}
          />

          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleMemberFormChange("lastName", e.target.value)}
            required
            fullWidth
            placeholder="Doe"
            error={!!validationErrors.lastName}
            helperText={validationErrors.lastName}
          />

          <ErrorTypography error={error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingWrappedNode loading={loading}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
          >
            Add Team Member
          </Button>
        </LoadingWrappedNode>
      </DialogActions>
    </Dialog>
  );
}
